<template>
    <b-form
            class="onboarding-section mx-auto"
            :class="{
            'organization-branding': hasOrganizationBranding,
        }"
            :style="onboardingFormStyle"
    >
        <div
                v-if="brandingLogo"
                class="text-center"
        >
            <b-img-lazy
                    :src="brandingLogo"
                    blank-color="#2b2b2b"
                    blank-height="80px"
                    height="80px"
                    class="mb-4 logo"
            />
        </div>
        <div
                v-else-if="brandingOrgName"
                class="text-center"
        >
            <p class="org-name font-weight-bold">
                {{ brandingOrgName }}
            </p>
        </div>
        <div
                v-else-if="logo"
                class="text-center"
        >
            <b-img-lazy
                    :src="logo"
                    blank-color="#2b2b2b"
                    blank-height="80px"
                    height="80px"
                    class="mb-4 logo"
            />
        </div>

        <p class="title mb-0 pt-4">
            Get Started
        </p>

        <p class="subtitle mx-auto mb-0">
            To save time, we've pre-filled some information, if available. Please update anything that's missing or
            incorrect.
        </p>

        <div class="form-box-with-info">
            <onboarding-form
                    :disabled="disabled"
                    :is-exclusive-member="isExclusiveMember"
                    :form-errors="formErrors"
                    :industries="industries"
                    v-model="form"
                    @submit="handleSubmit"
            />
            <div class="d-flex flex-column align-items-center align-items-md-start">
                <brought-by :brought-by-org-member="broughtByOrgMember" />
                <onboarding-info-card :class=" {'onboarding-info-card-margin' : !broughtByOrgMember}" />
            </div>
        </div>
    </b-form>
</template>

<script>
import "./OnboardingSection.sm.scss";
import "./OnboardingSection.scss";
import {mapGetters} from "vuex";
import FormErrorsMixin from "@milestoneslabs/milestones-ui-kit/src/components/FormErrors/FormErrorsMixin";
import parsePhoneNumber from 'libphonenumber-js';
import PasswordValidation from "@milestoneslabs/milestones-ui-kit/src/components/Onboarding/PasswordValidationTooltip.vue";
import {UUIDS} from "@milestoneslabs/milestones-core-js/src/AppResources/IndustryAppResource";
import {IDS} from "@milestoneslabs/milestones-core-js/src/AppResources/LuOrganizationOccupationAppResource";
import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
import {privacyPolicy, termsOfUse} from "../../../helpers/externalLinks";
import OnboardingInfoCard
    from '@milestoneslabs/milestones-ui-kit/src/components/Onboarding/OnboardingInfoCard/OnboardingInfoCard.vue';
import ColorBrandingMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ColorBrandingMixin";
import InternalFormErrosMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/InternalFormErrorsMixin";
import isEmpty from "lodash/isEmpty";
import {formatPhoneUS} from "../../../helpers/phoneHelper";
import BroughtBy from "@milestoneslabs/milestones-ui-kit/src/components/Onboarding/BroughtBy/BroughtBy.vue";
import OnboardingForm from "@milestoneslabs/milestones-ui-kit/src/components/Onboarding/OnboardingForm/OnboardingForm.vue"

export default {
    name: "OnboardingSection",
    components: {
        BroughtBy,
        PasswordValidation,
        OnboardingInfoCard,
        OnboardingForm,
    },

    mixins: [
        FormErrorsMixin,
        ShowNotificationMixin,
        ColorBrandingMixin,
        InternalFormErrosMixin
    ],

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        isExclusiveMember: {
            type: Boolean,
            default: false,
        },
        userAppResource: {
            type: Object,
            required: false,
        },
        broughtByOrgMember: {
            default: null,
        },
        industries: {
            type: Array
        },
    },

    data() {
        return {
            form: {
                firstName: null,
                lastName: null,
                email: null,
                phone: null,
                industry: UUIDS.REAL_ESTATE,
                occupation: IDS.REAL_ESTATE_AGENT,
                password: '',
                displayName: null,
                onboarding: 'completed',
                meta: {
                    accountType: 'professional',
                    company: '',
                },
                licenseState: null,
                licenseNumber: null,
                licenseType: null,
                licenseIssuingBody: null,
            },
            termsAccepted: false,
            phone: null,
            showPasswordTooltip: false,
            privacyPolicyLink: privacyPolicy,
            termsOfUseLink: termsOfUse,
            REAL_ESTATE_AGENT: IDS.REAL_ESTATE_AGENT,
            brandingLogo: undefined,
            brandingOrgName: undefined,
            logo: undefined,
        };
    },

    computed: {
        ...mapGetters({
            getOnboardingEmail: 'getOnboardingEmail',
        }),

        requestFilters() {
            return {pagination: 'disabled'};
        },

        validationRules() {
            return {
                firstName: {
                    type: 'string',
                    presence: true,
                    length: {
                        minimum: 1,
                        maximum: 255,
                        tooShort: 'is required!'
                    }
                },
                lastName: {
                    type: 'string',
                    presence: true,
                    length: {
                        minimum: 1,
                        maximum: 255,
                        tooShort: 'is required!'
                    }
                },
                email: {
                    type: 'string',
                    presence: true,
                    email: true,
                },
                phone: {
                    phoneNumber: {
                        country: 'US',
                        presence: true,
                    },
                },
                password: {
                    password: {
                        type: 'string',
                        presence: !this.isExclusiveMember
                    }
                },
            };
        },


        onboardingFormStyle() {
            return this.hasOrganizationBranding && this.hasBrandingPrimaryColor ? {
                '--primaryColor': this.organizationBrandingPrimaryColor,
                '--branding-primary-color': this.organizationBrandingPrimaryColor,
            } : {};
        },
    },

    beforeMount() {
        this.prefill();
        if (typeof sessionStorage.getItem('brandingLogo') === 'string') {
            this.brandingLogo = sessionStorage.getItem('brandingLogo');
        } else if (this.broughtByOrgMember?.organizationLogo) {
            this.brandingLogo = this.broughtByOrgMember.organizationLogo;
        }

        if (typeof sessionStorage.getItem('brandingOrgName') === 'string') {
            this.brandingOrgName = sessionStorage.getItem('brandingOrgName');
        } else if (this.broughtByOrgMember?.organizationName) {
            this.brandingOrgName = this.broughtByOrgMember.organizationName;
        }

        this.logo = location.origin + '/images/logo.png?v=1';
    },

    methods: {
        handleSubmit() {
            this.setFormData();

            this.clearErrors('lastName');
            this.clearErrors('firstName');

            this.validateUserCredentials();

            let errors = this.$validate(this.form, this.validationRules);
            errors = Object.assign(errors ? errors : {}, this.formErrors);
            this.setformErrors(errors);
            if (!isEmpty(errors)) {
                this.showNotification({
                    type: 'error',
                    message: 'Please complete the required fields!'
                });
                return;
            }

            if (this.form.occupation === IDS.LOAN_OFFICER) {
                if (
                        this.form.licenseIssuingBody &&
                        this.form.licenseState &&
                        this.form.licenseType &&
                        this.form.licenseNumber
                ) {
                    this.form.licenses = [
                        {
                            issuingBody: this.form.licenseIssuingBody,
                            state: this.form.licenseState,
                            type: this.form.licenseType,
                            number: this.form.licenseNumber,
                        }
                    ];
                }
                delete this.form.licenseIssuingBody;
                delete this.form.licenseState;
                delete this.form.licenseType;
                delete this.form.licenseNumber;
            }

            this.$emit('submit', this.form);
        },

        setFormData() {
            if (this.form.phone) {
                let phoneNumber = parsePhoneNumber(this.form.phone, 'US');
            }
        },

        prefill() {
            if (this.userAppResource) {
                let user = this.userAppResource;
                this.form.email = user.email;
                this.form.firstName = user.firstName;
                this.form.lastName = user.lastName;
                this.form.phone = user.phone ? formatPhoneUS(user.phone) : null;
                this.form.occupation = this.getUserOccupation();
                this.form.industry = this.getUserIndustry();

                return;
            }

            const url = new URL(window.location.href);
            const queryParams = new URLSearchParams(url.search);

            if (url.pathname.includes('/get-started')) {
                this.form.email = queryParams.get('email') || null;

                return;
            }

            this.form.email = sessionStorage.getItem('onboardingEmail');
        },
        getUserOccupation() {
            return this.getUserOrganizationDetail('organizationOccupationId', IDS.REAL_ESTATE_AGENT);
        },

        getUserIndustry() {
            return this.getUserOrganizationDetail('organizationIndustryUuid', UUIDS.REAL_ESTATE);
        },

        getUserOrganizationDetail(detailKey, defaultValue) {
            if (this.userAppResource && this.userAppResource.organizationMemberships) {
                const selfOrg = this.userAppResource.organizationMemberships.toArray().find(
                        item => item.organization.type === 'self'
                );
                return selfOrg?.[detailKey] ?? defaultValue;
            }
            return defaultValue;
        },
    },
};
</script>
